import { createLatestOnlyWriter } from './latestOnlyWriter';

/**
 * Sincronizações da navegação com o servidor que o usuário pode disparar em rajada
 * (arrastar abas fixadas, alternar tema). Regras comuns:
 * - gravações em série, só a intenção mais recente vai ao servidor;
 * - toda intenção nova incrementa uma revisão; respostas tardias (recuperação,
 *   rollback) só mexem na tela se a revisão ainda for a mesma.
 */

export interface FavoriteRef {
  path: string;
  id: string;
  order_index: number;
}

interface PinnedOrderSyncDeps {
  /** Ids conhecidos localmente, por path. */
  getKnownIds: () => Map<string, string>;
  /** Sincronização em andamento do favorito desse path (fixar/desfixar há pouco), se houver. */
  getPendingSync: (path: string) => Promise<unknown> | undefined;
  fetchFavorites: () => Promise<FavoriteRef[]>;
  /** Atualiza os ids locais com o que o servidor devolveu. */
  rememberFavorites: (favorites: FavoriteRef[]) => void;
  reorder: (ids: string[]) => Promise<unknown>;
  /** Reaplica na tela a ordem que o servidor tem. */
  restoreServerOrder: (paths: string[]) => void;
  onError?: (error: unknown) => void;
}

export class IncompleteFavoritesError extends Error {
  constructor(public readonly missingPaths: string[]) {
    super(`Favoritos sem id: ${missingPaths.join(', ')}`);
  }
}

/** Ordem das abas fixadas: nunca envia lista parcial e nunca desfaz uma ordem mais nova. */
export function createPinnedOrderSync(deps: PinnedOrderSyncDeps) {
  let revision = 0;

  const resolveIds = async (paths: string[]): Promise<string[]> => {
    // Espera a sincronização de TODOS os paths, inclusive os de id conhecido: um
    // desfixar → fixar troca o id (DELETE + POST) e o antigo deixaria de existir.
    // Uma sincronização pode encadear outra (nova intenção durante a anterior): espera
    // até não sobrar nenhuma nova.
    const awaited = new Set<Promise<unknown>>();
    for (;;) {
      const pending = paths
        .map((path) => deps.getPendingSync(path))
        .filter((sync): sync is Promise<unknown> => Boolean(sync) && !awaited.has(sync as Promise<unknown>));
      if (!pending.length) break;
      pending.forEach((sync) => awaited.add(sync));
      await Promise.allSettled(pending);
    }
    let known = deps.getKnownIds();
    const missing = () => paths.filter((path) => !known.get(path));

    if (missing().length) {
      const favorites = await deps.fetchFavorites();
      deps.rememberFavorites(favorites);
      known = deps.getKnownIds();
    }
    const stillMissing = missing();
    if (stillMissing.length) throw new IncompleteFavoritesError(stillMissing);
    return paths.map((path) => known.get(path) as string);
  };

  const write = createLatestOnlyWriter<string[]>(
    async (paths) => {
      const ids = await resolveIds(paths);
      await deps.reorder(ids);
    },
    (error) => {
      deps.onError?.(error);
      const failedRevision = revision;
      deps.fetchFavorites()
        .then((favorites) => {
          // O usuário mexeu de novo enquanto recuperávamos: a ordem nova manda.
          if (failedRevision !== revision) return;
          deps.rememberFavorites(favorites);
          deps.restoreServerOrder(
            [...favorites].sort((a, b) => a.order_index - b.order_index).map((favorite) => favorite.path),
          );
        })
        .catch(() => { /* Sem servidor, mantém a ordem local. */ });
    },
  );

  return (pinnedPaths: string[]) => {
    revision += 1;
    if (pinnedPaths.length > 1) write(pinnedPaths);
  };
}

interface ThemeSyncDeps<T> {
  persist: (value: T) => Promise<unknown>;
  apply: (value: T) => void;
  /** A gravação mais recente falhou e a tela voltou ao último valor confirmado. */
  onRollback?: (error: unknown) => void;
}

/**
 * Preferência alternável (tema): aplica na hora, grava em série só a escolha mais
 * recente e, se ela falhar, volta ao último valor que o servidor confirmou — a menos
 * que o usuário já tenha escolhido de novo.
 */
export interface PreferenceSync<T> {
  set: (next: T, previous: T) => void;
  /**
   * Início de uma leitura do valor gravado por outro caminho (ex.: tela Preferências).
   * Devolve quem confirma o valor lido; a confirmação é ignorada se, desde o início da
   * leitura, o usuário escolheu por este controle ou outra leitura começou.
   */
  beginExternalRead: () => (value: T) => void;
}

export function createPreferenceSync<T>(deps: ThemeSyncDeps<T>): PreferenceSync<T> {
  let revision = 0;
  let confirmed: T | undefined;
  let confirmedRevision = 0;
  let readSeq = 0;

  const write = createLatestOnlyWriter<{ value: T; revision: number }>(
    async ({ value, revision: writtenRevision }) => {
      await deps.persist(value);
      if (writtenRevision >= confirmedRevision) {
        confirmed = value;
        confirmedRevision = writtenRevision;
      }
    },
    (error) => {
      // latestOnlyWriter só reporta a falha quando não há escolha mais nova na fila,
      // então voltar ao último valor confirmado não desfaz nada que o usuário fez depois.
      if (confirmed !== undefined) deps.apply(confirmed);
      deps.onRollback?.(error);
    },
  );

  return {
    /** Escolha do usuário por este controle: aplica na hora e grava. */
    set: (next: T, previous: T) => {
      if (confirmed === undefined) {
        // Antes da primeira gravação, o valor exibido é o confirmado.
        confirmed = previous;
      }
      revision += 1;
      deps.apply(next);
      write({ value: next, revision });
    },
    beginExternalRead: () => {
      const startedAt = { revision, read: (readSeq += 1) };
      return (value: T) => {
        if (startedAt.revision !== revision || startedAt.read !== readSeq) return;
        confirmed = value;
        confirmedRevision = revision;
      };
    },
  };
}
